import { compact, filter, find, get, isEmpty, omit, reduce } from 'lodash-es';
import { LICENSE_PLATE_AND_VIN, INSTANT_OFFER } from 'site-modules/shared/constants/appraisal/appraisal';
import { PARTNERS } from 'site-modules/shared/constants/multi-offer/partners';
import {
  CARWISER_MULTIPLE_ANSWERS,
  CHANGE_ACTION_TYPE,
  CONDITION_QUESTION_ANSWER_TYPE,
  getDefaultAnswer as _getDefaultAnswer,
  filterCarwiserQuestions,
} from 'site-modules/shared/components/appraisal/condition-questions/constants/utils';

/**
 * Should determine vin eligibility only if make object, model object, year object and vin(in query parameter) is available.
 * @param {Object} makeObj - make object
 * @param {Object} modelObj - model object
 * @param {Object} yearObj - year object
 * @param {String} predefinedVin - predefined vin from query param or insider profile history
 * @return {Boolean}
 */
export const shouldDetermineVinEligibility = (
  makeObj = {},
  modelObj = {},
  yearObj = {},
  predefinedVin,
  isVinFromDrawer,
  activeStepId
) => {
  const { name: makeName } = makeObj;
  const { name: modelName } = modelObj;
  const { year } = yearObj;

  // Avoid race conditions: abort making the check if we *just* left to/after VIN/LP step, which runs the same checks:
  if ([LICENSE_PLATE_AND_VIN, INSTANT_OFFER].includes(activeStepId)) {
    return false;
  }

  return compact([makeName, modelName, year, predefinedVin || isVinFromDrawer]).length === 4;
};

// This accounts for scenarios when new questions are added. When there are existing responses stored
// in MODS, this checks that there is a corresponding answer for all questions displayed to the user.
export const areAllRequiredQuestionsAnswered = ({ answersMap, partner, questionsFlatten }) => {
  const flattenedQuestionsByPartner = partner
    ? questionsFlatten.filter(question => question.partner === partner)
    : questionsFlatten;
  const questionsWithRequiredAnswers = flattenedQuestionsByPartner.filter(
    question => question.answerType === CONDITION_QUESTION_ANSWER_TYPE.SINGLE
  );

  return questionsWithRequiredAnswers.every(({ id, isSubQuestion, parentQuestionId, parentAnswerId }) => {
    if (isSubQuestion && answersMap.has(parentQuestionId)) {
      // if subquestion, find the corresponding parent question to see if it was answered
      const parentAnswers = answersMap.get(parentQuestionId);

      // this means that the parent question was answered, exposing the subquestion, so the subquestion requires an answer
      if (parentAnswers[0] === parentAnswerId) {
        return answersMap.has(id);
      }

      // this means that this question was not exposed, and thus does not require an answer
      return true;
    }
    // otherwise, just confirm that there is a corresponding answer for the question
    return answersMap.has(id);
  });
};

/**
 * Returns if the condition questions should be pre-populated with the given CarMax/Carwiser answers
 * from MODS (i.e. Multi-Offer Data Store).
 *
 * @param {Array} kmxAnswers CarMax answers from MODS.
 * @param {Array} carwiserAnswers Carwiser answers from MODS.
 * @param {Array} questionsFlatten flattened condition questions.
 * @returns {boolean}
 */
export const shouldPrepopulateAnswersFromMods = ({ kmxAnswers = [], carwiserAnswers = [], questionsFlatten }) => {
  const answersMap = new Map();
  [...kmxAnswers, ...carwiserAnswers].forEach(({ id, answers }) => {
    if (!answersMap.has(id)) answersMap.set(id, answers);
  });

  // Pre-populate Multi-Offer condition questions only if we have answers to ALL questions (see EMO-2025).
  return (
    !isEmpty(kmxAnswers) &&
    areAllRequiredQuestionsAnswered({
      answersMap,
      partner: PARTNERS.CARWISER,
      questionsFlatten,
    })
  );
};

/**
 * Returns if the condition questions should be pre-populated with the given V2 answers from MODS (i.e. Multi-Offer Data Store).
 *
 * @param {Object} v2Answers V2 answers from MODS.
 * @param {Array} questionsFlatten flattened condition questions.
 * @returns {boolean}
 */
export const shouldPrepopulateV2AnswersFromMods = (v2Answers = {}, questionsFlatten) => {
  const answersMap = new Map(Object.entries(v2Answers));

  // Pre-populate Multi-Offer condition questions only if we have answers to ALL questions.
  return areAllRequiredQuestionsAnswered({
    answersMap,
    questionsFlatten,
  });
};

/**
 * Generates an array with "verbose" object(s) for the given answer.
 *
 * @param {Object|Number|String} questionsAnsweredValue selected answer.
 * @param {Object} question the corresponding question object.
 * @returns {Object[]}
 */
function generateVerboseArrayFromAnswer(questionsAnsweredValue, question) {
  if (!questionsAnsweredValue) {
    return [];
  }

  const answerArray =
    // If answerType is 'multiple' then the answer is an object.
    question.answerType === CONDITION_QUESTION_ANSWER_TYPE.MULTIPLE
      ? reduce(
          questionsAnsweredValue,
          (resultArray, isSelected, id) => {
            if (isSelected) {
              // NOTE: id is a string here, because object keys are always strings.
              resultArray.push(id);
            }
            return resultArray;
          },
          []
        )
      : [questionsAnsweredValue];

  return question.answers
    .filter(({ id }) => answerArray.find(id2 => `${id}` === `${id2}`))
    .map(({ id, desc }) => ({ id, desc }));
}

/**
 * Generates the expected "verbose" answer object needed to construct payload, validation, and other business logic.
 * @param {Object} question - Entire question object to gather details
 * @param {Object|Number|String} questionsAnsweredValue - selected answer
 * @returns {Object}
 *    @param {Boolean} isOptional - Optional question field; can be used for validation.
 *      i.e. If "answers" is empty or undefined and the "isOptional" flag is true, we can force it to be valid.
 *    @param {String} desc - Question description
 *    @param {Array of Object} answers - The answers that were selected (if there are any)
 */
export function generateVerbosePayloadForQuestion(question, questionsAnsweredValue) {
  const { answerType, desc, parentQuestionId, parentAnswerId, isSubQuestion, partner, isOptional } = question;

  return {
    isOptional: isOptional || answerType === CONDITION_QUESTION_ANSWER_TYPE.MULTIPLE,
    desc,
    answers: generateVerboseArrayFromAnswer(questionsAnsweredValue, question),
    parentQuestionId,
    parentAnswerId,
    isSubQuestion,
    partner,
  };
}

/**
 * Returns verbose payload for the given questions & answers.
 *
 * @param {Array} questionsUngrouped condition questions as returned from API (i.e. NOT flatten).
 * @param {Object} questionsAnswered current answers to the questions.
 * @returns {Object} verbose payload.
 */
export const buildVerbosePayload = ({ questionsUngrouped, questionsAnswered }) => {
  const result = {};
  questionsUngrouped.forEach(parentQuestion => {
    const parentQuestionId = parentQuestion.id;
    const questionAnswered = questionsAnswered[parentQuestionId];
    result[parentQuestionId] = generateVerbosePayloadForQuestion(parentQuestion, questionAnswered);
  });
  return result;
};

export const isQuestionAnswered = ({ questionId, questionsAnswered }) => !!questionsAnswered?.[questionId];

/**
 * Returns the verbose answer entry for a single question id.
 *
 * @param {String|Number} id - Question id to look up.
 * @param {Array} questionsFlatten - Flattened list of all questions.
 * @param {Object} questionsAnswered - Current answers keyed by question id.
 * @returns {Object} Verbose payload entry.
 */
export const getVerboseAnswerById = (id, questionsFlatten, questionsAnswered) => {
  // Sometimes the "id" is passed as string (especially when Object.keys is used). Safer to do object literal to convert to string.
  const question = find(questionsFlatten, q => `${q.id}` === `${id}`);
  return generateVerbosePayloadForQuestion(question, questionsAnswered[id]);
};

/**
 * Rebuilds verbosePayload after an answer selection, handling ADD/REMOVE of detail questions.
 *
 * @param {Object} verbosePayload - Current verbose payload.
 * @param {Object} action - Action descriptor from the onChange handler.
 * @param {Function} getVerboseAnswer - Callback `(id) => verbosePayloadEntry` for a single question id.
 * @returns {Object} Updated verbose payload.
 */
export const buildUpdatedVerbosePayload = ({ verbosePayload, action = {}, getVerboseAnswer }) => {
  const updatedVerbosePayload = {};
  const { actionType, detailQuestionsAnswerTypeById = new Map() } = action;
  const detailQuestionsIdList = Array.from(detailQuestionsAnswerTypeById.keys());

  switch (actionType) {
    case CHANGE_ACTION_TYPE.ADD:
      // Adds detail questions to offer payload
      Object.keys(verbosePayload)
        .concat(detailQuestionsIdList)
        .forEach(id => {
          updatedVerbosePayload[id] = getVerboseAnswer(id);
        });
      break;
    case CHANGE_ACTION_TYPE.REMOVE:
      // Removes detail questions from offer payload
      Object.keys(omit(verbosePayload, detailQuestionsIdList)).forEach(id => {
        updatedVerbosePayload[id] = getVerboseAnswer(id);
      });
      break;
    default:
      // Updates offer payload with the selected answer
      Object.keys(verbosePayload).forEach(id => {
        updatedVerbosePayload[id] = getVerboseAnswer(id);
      });
  }
  return updatedVerbosePayload;
};

export const fixCarwiserSavedResponses = (savedResponseMap, carwiserQuestionsFlatten) => {
  const result = { ...savedResponseMap };

  // Carwiser saved responses do NOT contain the questions with the MULTIPLE answer type (e.g. "visibleDefectsChildren"
  // question). Instead, they contain the ANSWERS for these questions (i.e. "visibleDefectsDings" answer).
  // NOTE: please see the EMO-164 story changes for details.
  carwiserQuestionsFlatten.forEach(({ id, answerType, answers }) => {
    if (answerType === CONDITION_QUESTION_ANSWER_TYPE.MULTIPLE) {
      if (result[id] === undefined) {
        // if there is NO saved response for a question with the multiple answer type, then build this response from the
        // saved responses for the answers of this question.
        result[id] = answers
          .map(({ id: answerId }) => answerId)
          .filter(answerId => {
            const response = result[answerId];
            return get(response, '[0]') === CARWISER_MULTIPLE_ANSWERS.SELECTED.id;
          });
      }
    }
  });

  return result;
};

export function getDefaultAnswer(question, isSubQuestion, savedResponses) {
  const { answerType, answers = [] } = question;

  if (isSubQuestion || answerType !== CONDITION_QUESTION_ANSWER_TYPE.SINGLE) {
    return _getDefaultAnswer(answerType, answers);
  }
  // If we are here, then the question is top-level and doesn't have saved answer.
  // Check if parent question has child answers (EVAL-3751).

  const answerWithChildAnswers = answers.find(
    ({ detailQuestions }) => detailQuestions && detailQuestions.find(({ id }) => savedResponses[id])
  );

  if (answerWithChildAnswers) {
    return answerWithChildAnswers.id;
  }
  return _getDefaultAnswer(answerType, answers);
}

export function prepopulateConditionQuestion(savedResponses, question, isSubQuestion = false) {
  const { id, answerType } = question;
  const savedResponseAnswers = savedResponses[id] || [];
  const result =
    answerType === CONDITION_QUESTION_ANSWER_TYPE.SINGLE && !!savedResponseAnswers.length
      ? savedResponseAnswers[0]
      : getDefaultAnswer(question, isSubQuestion, savedResponses);

  if (answerType === CONDITION_QUESTION_ANSWER_TYPE.MULTIPLE) {
    savedResponseAnswers.forEach(anws => {
      result[anws] = true;
    });
  }
  return result;
}

export const getPrepopulateData = ({ carmaxResponses, carwiserResponses, questionsFlatten }) => {
  const allResponses = [...(carmaxResponses || []), ...(carwiserResponses || [])];
  let savedResponseMap = allResponses.reduce(
    (result, { id, answers }) => ({
      ...result,
      [id]: answers,
    }),
    {}
  );
  if (!isEmpty(carwiserResponses)) {
    savedResponseMap = fixCarwiserSavedResponses(savedResponseMap, filterCarwiserQuestions(questionsFlatten));
  }

  const partnerToResponsesMap = {
    [PARTNERS.CARMAX]: carmaxResponses,
    [PARTNERS.CARWISER]: carwiserResponses,
  };
  const partnersWithResponses = Object.entries(partnerToResponsesMap)
    .filter(([, responses]) => !isEmpty(responses))
    .map(([partner]) => partner);
  const questionsToPrepopulate = filter(questionsFlatten, ({ partner }) => partnersWithResponses.includes(partner));

  return { questionsToPrepopulate, savedResponseMap };
};
